import { Divider, Grid, TextInput } from '@mantine/core';
import { AnimatePresence, motion } from 'framer-motion';
import { flow, set } from 'lodash/fp';
import React from 'react';

import {
  ProductUsageBasedFieldsNames,
  useFeatureFlags,
} from '@portals/api/partners';
import {
  CreditCard,
  Gauge,
  RoundDollarWithCursor,
} from '@portals/framework/icons';
import { ProductPricingModel } from '@portals/types';

import { DetailsColumnContent } from '../common/DetailsColumnContent';
import { RadioCard } from '../common/RadioCard';
import { RadioCardGroup } from '../common/RadioCardGroup';
import { RadioCardLabel } from '../common/RadioCardLabel';
import { ProductWizardContext, StepId } from '../types';
import { useProductWizard } from '../use-product-wizard';

export function PricingModelStep() {
  const { contextData, setContextData } = useProductWizard();
  const featureFlags = useFeatureFlags();

  return (
    <DetailsColumnContent title="Pricing Model">
      <DetailsColumnContent.Section title="Choose a pricing model">
        <RadioCardGroup
          error={contextData.errors[StepId.PricingModel]?.general}
          value={contextData.product.pricing_model}
          onChange={(value) =>
            setContextData((prev) =>
              flow([
                set(['product', 'pricing_model'], value),
                set(['errors', StepId.PricingModel], ''),
              ])(prev)
            )
          }
        >
          <RadioCard
            value={ProductPricingModel.Standard}
            label={
              <RadioCardLabel
                title="Standard pricing"
                description="Charges customers a pre defined amount for a product or service, offering predictable cost."
                icon={<CreditCard />}
              />
            }
          />

          {featureFlags?.usage_based_pricing && (
            <RadioCard
              value={ProductPricingModel.UsageBased}
              label={
                <RadioCardLabel
                  title="Usage Based pricing"
                  description="Charges customers based on usage, offering a cost-effective alternative to fixed pricing."
                  icon={<Gauge />}
                >
                  <UsageBasedFields />
                </RadioCardLabel>
              }
            />
          )}

          {featureFlags?.personalized_pricing_model && (
            <RadioCard
              value={ProductPricingModel.Personalized}
              label={
                <RadioCardLabel
                  title="Personalized pricing"
                  description="Your customers will manually enter the price for a product or service."
                  icon={<RoundDollarWithCursor />}
                />
              }
            />
          )}
        </RadioCardGroup>
      </DetailsColumnContent.Section>
    </DetailsColumnContent>
  );
}

type PricingModelUsageBasedErrorKeys = keyof Pick<
  ProductWizardContext['errors'][StepId.PricingModel],
  'usageBasedDisplayName' | 'usageBasedTelemetryKey' | 'usageBasedUnitName'
>;
function UsageBasedFields() {
  const { contextData, setContextData } = useProductWizard();

  const onChange = (params: {
    fieldKey: ProductUsageBasedFieldsNames;
    errorKey: PricingModelUsageBasedErrorKeys;
    value: string;
  }) => {
    const { fieldKey, errorKey, value } = params;

    setContextData((prev) =>
      flow([
        set(['product', fieldKey], value),
        set(['errors', StepId.PricingModel, errorKey], ''),
      ])(prev)
    );
  };

  return (
    <AnimatePresence initial={false}>
      {contextData.product.pricing_model === ProductPricingModel.UsageBased && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
        >
          <Divider mb="md" />

          <Grid columns={12}>
            <Grid.Col xs={12} xl={5}>
              <TextInput
                required
                autoFocus
                label="Display name"
                data-testid="usage-based-pricing-display-name"
                placeholder="Distance"
                error={
                  contextData.errors[StepId.PricingModel]?.usageBasedDisplayName
                }
                value={contextData.product.usage_based_display_name ?? ''}
                onChange={({ target }) =>
                  onChange({
                    fieldKey: 'usage_based_display_name',
                    errorKey: 'usageBasedDisplayName',
                    value: target.value,
                  })
                }
              />
            </Grid.Col>

            <Grid.Col xs={7} xl={5}>
              <TextInput
                required
                label="Telemetry"
                data-testid="usage-based-pricing-telemetry"
                placeholder="distance_traveled"
                error={
                  contextData.errors[StepId.PricingModel]
                    ?.usageBasedTelemetryKey
                }
                value={contextData.product.usage_based_telemetry_key ?? ''}
                onChange={({ target }) =>
                  onChange({
                    fieldKey: 'usage_based_telemetry_key',
                    errorKey: 'usageBasedTelemetryKey',
                    value: target.value,
                  })
                }
              />
            </Grid.Col>

            <Grid.Col xs={5} xl={2}>
              <TextInput
                required
                label="Unit"
                placeholder="km"
                data-testid="usage-based-pricing-unit"
                error={
                  contextData.errors[StepId.PricingModel]?.usageBasedUnitName
                }
                value={contextData.product.usage_based_unit_name ?? ''}
                onChange={({ target }) =>
                  onChange({
                    fieldKey: 'usage_based_unit_name',
                    errorKey: 'usageBasedUnitName',
                    value: target.value,
                  })
                }
              />
            </Grid.Col>
          </Grid>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
